/**
 * Planning half of frozen-cart recovery.
 *
 * The delivery-date validation function (stocked-extensions,
 * extensions/delivery-date-validation) is evaluated against the cart as it
 * exists when a write ARRIVES, not against the cart the write would produce.
 * So once a cart is in a state the function rejects, EVERY storefront mutation
 * is refused with a 422 -- removing a line, clearing the cart, even writing a
 * fresh valid delivery date. The buyer cannot edit their way out, and the state
 * arrives on its own: a delivery date goes past its cutoff simply because days
 * passed, and a product's availability weeks roll behind the horizon.
 *
 * The only escape is to abandon the cart token and rebuild (see
 * StockedDelivery.recoverCart(), assets/delivery-date.js). This module decides
 * WHAT that rebuilt cart should contain: which lines can still be delivered,
 * and the one delivery date to put them on.
 *
 * The output must describe a cart the function ACCEPTS, or recovery just mints
 * a second frozen cart. That means the chosen date has to work for every kept
 * line at once, and when no date can save any constrained line, the plan
 * carries no date at all rather than a doomed one (an undated cart with no
 * non-exempt lines is exactly what the function's requiresDelivery short-circuit
 * lets through).
 *
 * Pure and DOM-free so it's require()-able in node:test, mirroring
 * assets/cart-reconcile.js. Its dependencies (parseAvailability,
 * availabilityFor, slotOf) are themselves pure, UMD-exported functions from
 * assets/delivery-date-format.js.
 */
(function (root, factory) {
  var fmt = (typeof module !== 'undefined' && module.exports)
    ? require('./delivery-date-format.js')
    : root.StockedDeliveryFormat;
  var api = factory(fmt);
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  else root.StockedCartRecovery = api;
})(typeof self !== 'undefined' ? self : this, function (fmt) {
  // Ordered, de-duplicated slots (week codes, or the ISO of a special date) across the offerable horizon, each mapped
  // to the EARLIEST offerable date inside it. Recovery rebuilds onto a week, but
  // the cart attribute the function reads is a specific date, so the week has to
  // carry one back. Insertion order is preserved, and selectableDates() emits
  // earliest-first, so iterating this is iterating the horizon in order.
  function weeksInHorizon(selectable, config) {
    var list = Array.isArray(selectable) ? selectable : [];
    var order = [];
    var earliest = {};
    var offered = {};                    // every offerable date, for preferredISO lookups
    for (var i = 0; i < list.length; i += 1) {
      var iso = list[i] && list[i].iso;
      var code = fmt.slotOf(iso, config);
      if (!code) continue;               // unparseable entry -- ignore, don't throw
      offered[iso] = true;
      if (!(code in earliest)) {
        earliest[code] = iso;
        order.push(code);
      }
    }
    return { order: order, earliest: earliest, offered: offered };
  }

  // key -> parsed availability, for the NON-EXEMPT lines only. Exempt lines are
  // omitted from data-cart-availability entirely (see
  // snippets/delivery-date-selector.liquid), so "absent from this map" is the
  // signal that a line is exempt and constrains nothing.
  function availabilityIndex(availability) {
    var list = Array.isArray(availability) ? availability : [];
    var index = {};
    for (var i = 0; i < list.length; i += 1) {
      var entry = list[i];
      if (!entry || !entry.key) continue;
      index[entry.key] = fmt.parseAvailability({
        startWeek: entry.start,
        endWeek: entry.end,
        weeks: entry.weeks,
        dates: entry.dates,
      });
    }
    return index;
  }

  /**
   * @param {object} input
   * @param {Array}  input.items         /cart.js line items (key, id, quantity, properties)
   * @param {Array}  input.availability  cartLinesRaw() output for the non-exempt lines
   * @param {Array}  input.selectable    selectableDates() entries, earliest first
   * @param {string} [input.preferredISO] the date the cart is already on
   * @param {object} [input.config]      delivery config (special_dates); absent -> week codes only
   * @returns {{targetISO: string|null, targetWeek: string|null, keep: Array, drop: Array}}
   *   `targetWeek` holds the chosen SLOT: a week code, or the ISO of a special date.
   *   `keep`/`drop` hold the ORIGINAL item objects, so quantity and line item
   *   properties survive the rebuild untouched.
   */
  function planRecovery(input) {
    var opts = input || {};
    var items = (Array.isArray(opts.items) ? opts.items : []).filter(Boolean);
    var index = availabilityIndex(opts.availability);
    var horizon = weeksInHorizon(opts.selectable, opts.config);

    var constrained = [];
    var exempt = [];
    for (var i = 0; i < items.length; i += 1) {
      if (items[i].key && Object.prototype.hasOwnProperty.call(index, items[i].key)) constrained.push(items[i]);
      else exempt.push(items[i]);
    }

    // Nothing that needs delivering: the rebuilt cart requires no date, and the
    // validation function short-circuits on it.
    if (!constrained.length) {
      return { targetISO: null, targetWeek: null, keep: items, drop: [] };
    }

    // The date the cart is already on, but only if it is still offerable -- a
    // stale one (the whole reason recovery may be running) can't be preferred.
    var preferredISO = null;
    if (opts.preferredISO && horizon.offered[opts.preferredISO]) preferredISO = opts.preferredISO;
    var preferredWeek = preferredISO ? fmt.slotOf(preferredISO, opts.config) : null;

    // Score every offerable week by how many constrained lines it saves. Most
    // saved wins, and the customer's own date is only overridden when another
    // week saves STRICTLY more -- recovery exists to unstick the cart, not to
    // move a delivery the customer already chose. The horizon is walked
    // earliest-first, so among weeks the customer has no stake in, ties keep the
    // earliest without a separate tie-break. A week that saves nothing is never
    // chosen -- `best` stays null and the no-date branch below takes over.
    var best = null;
    for (var w = 0; w < horizon.order.length; w += 1) {
      var code = horizon.order[w];
      var saved = [];
      for (var c = 0; c < constrained.length; c += 1) {
        if (fmt.availabilityFor(index[constrained[c].key], code) === 'available') saved.push(constrained[c]);
      }
      if (!saved.length) continue;
      var isPreferred = code === preferredWeek;
      var beats = !best
        || saved.length > best.saved.length
        || (isPreferred && saved.length === best.saved.length);
      // Keeping the customer's exact DAY matters as much as the week: their week
      // may start on the 13th while they deliberately chose the 14th.
      if (beats) best = { code: code, iso: isPreferred ? preferredISO : horizon.earliest[code], saved: saved };
    }

    // No week saves a single constrained line (the horizon is empty, or every
    // line's availability has rolled past it). A dated rebuild would be born
    // frozen, so rebuild the exempt lines only, undated.
    if (!best) {
      return { targetISO: null, targetWeek: null, keep: exempt, drop: constrained };
    }

    var kept = {};
    for (var k = 0; k < best.saved.length; k += 1) kept[best.saved[k].key] = true;
    return {
      targetISO: best.iso,
      targetWeek: best.code,
      keep: exempt.concat(best.saved),
      drop: constrained.filter(function (item) { return !kept[item.key]; }),
    };
  }

  return { planRecovery: planRecovery };
});
